<script lang="ts" setup>
const route = useRoute()

const { breakpoint } = useApp()
const { public: config } = useRuntimeConfig()
const ignoreParamsPattern = new RegExp(config.ignoreParamsPattern)

const props = withDefaults(defineProps<{
  currentPage: number
  totalPages: number
  surroundActivePage?: number
  showMaxPages?: boolean
  showDelimiter?: boolean
}>(), {
  surroundActivePage: 2,
  showMaxPages: true,
  showDelimiter: true,
})

const { currentPage, totalPages } = toRefs(props)

const emit = defineEmits(['updatePage'])

const surroundActivePage = ref(props.surroundActivePage)

const updatePage = (page: number) => {
  emit('updatePage', page)
  scrollToTop()
}

const previous = () => {
  if (currentPage.value === 1) {
    return
  }

  emit('updatePage', currentPage.value - 1)
  scrollToTop()
}

const next = () => {
  if (currentPage.value >= totalPages.value) {
    return
  }

  emit('updatePage', currentPage.value + 1)
  scrollToTop()
}

function inRange(page: number) {
  return (page >= (parseInt(currentPage.value) - surroundActivePage.value))
    && (page <= (parseInt(currentPage.value) + surroundActivePage.value))
}

const getParams = computed<string>(() => {
  return Object.entries(route.query).filter(([k]) => {
    return k && !ignoreParamsPattern.test(k)
  }).map(([k, v]) => {
    return k && v ? k + '=' + v : ''
  }).join('&')
})

function pageUrl(page: number) {
  const params = new URLSearchParams(getParams.value)

  params.delete('p')
  if (page !== 1) {
    params.set('p', `${page}`)
  }

  return route.path + (params.toString().length > 0 ? '?' + params.toString() : '')
}

onMounted(() => {
  watchEffect(() => {
    if (['mobile', 'phablet'].includes(breakpoint.value)) {
      surroundActivePage.value = 1
    }
  })
})
</script>

<template>
  <nav
    class="pages"
    aria-label="Pagination"
  >
    <ul class="pages__list">
      <li
        v-if="currentPage > 1"
        class="pages__item pages__item--previous"
      >
        <button
          type="button"
          aria-label="go to previous page"
          class="pages__prev"
          @click.prevent="previous()"
        >
          <NuxtIcon
            name="icon:arrow"
            class="icon:arrow-left"
          />
          <span>{{ $t('Previous') }}</span>
        </button>
      </li>

      <li
        v-if="currentPage > 1 + surroundActivePage && props.showMaxPages"
        class="pages__item pages__item--first"
      >
        <AppLink
          :to="pageUrl(1)"
          class="pages__link"
          @click="scrollToTop"
        >
          1
        </AppLink>
      </li>
      <li
        v-if="currentPage > 1 + surroundActivePage + (props.showMaxPages ? 2 : 1) && props.showDelimiter"
        class="pages__item pages__item--filler"
      >
        <span class="pages__delimiter">...</span>
      </li>
      <li
        v-else-if="currentPage > 1 + surroundActivePage + (props.showMaxPages ? 1 : 0) && props.showDelimiter"
        class="pages__item pages__item--filler"
      >
        <AppLink
          :to="pageUrl(currentPage - surroundActivePage - 1)"
          class="pages__link"
          aria-disabled="true"
          @click="scrollToTop"
        >
          {{ currentPage - surroundActivePage - 1 }}
        </AppLink>
      </li>

      <template
        v-for="page in totalPages"
        :key="page"
      >
        <li
          v-if="inRange(page)"
          :class="{ 'pages__item--current': parseInt(currentPage) === page }"
          class="pages__item"
        >
          <AppLink
            :to="pageUrl(page)"
            class="pages__link"
            :aria-label="'go to page ' + page"
            :aria-current="parseInt(currentPage) === page"
            @click.prevent="updatePage(page)"
          >
            <span>{{ page }}</span>
          </AppLink>
        </li>
      </template>

      <li
        v-if="currentPage < totalPages - surroundActivePage - (props.showMaxPages ? 2 : 1) && props.showDelimiter"
        class="pages__item pages__item--filler"
      >
        <span class="pages__delimiter">...</span>
      </li>
      <li
        v-else-if="currentPage < totalPages - surroundActivePage - (props.showMaxPages ? 1 : 0) && props.showDelimiter"
        class="pages__item pages__item--filler"
      >
        <AppLink
          :to="pageUrl(currentPage + surroundActivePage + 1)"
          class="pages__link"
          @click="scrollToTop"
        >
          {{ currentPage + surroundActivePage + 1 }}
        </AppLink>
      </li>
      <li
        v-if="currentPage < totalPages - surroundActivePage && props.showMaxPages"
        class="pages__item pages__item--last"
      >
        <AppLink
          :to="pageUrl(totalPages)"
          class="pages__link"
          @click="scrollToTop"
        >
          {{ totalPages }}
        </AppLink>
      </li>
      <li
        v-if="currentPage < totalPages"
        class="pages__item pages__item--next"
      >
        <button
          type="button"
          aria-label="go to next page"
          class="pages__prev"
          @click.prevent="next()"
        >
          <span>{{ $t('Next') }}</span>
          <NuxtIcon
            name="icon:arrow"
            class="icon:arrow-right"
          />
        </button>
      </li>
    </ul>
  </nav>
</template>

<style>
@import url("~layers/components/Paginator/Pages.css");
</style>
